import { ContextMenu as HMLContextMenu } from 'components/horizontal-move-list/context-menu/config/types';
import { ObjectUtil } from '@chesscom/web-ui';
import { Annotations } from 'chessboard/model/annotation/types';
import { MoveList } from 'chessboard/model/move-list/types';
import { CONTEXT_MENU_ACTION_NAMES } from 'components/move-list/components/context-menu/config/context-menu-action-names';

const { invertObject } = ObjectUtil;

/**
 * Object mapping NAG format to HTML display
 */
export const nagMap = {
  $1: '!',
  $2: '?',
  $3: '!!',
  $4: '??',
  $5: '!?',
  $6: '?!',
  $9: '☒',
  $10: '=',
  $11: '=',
  $12: '=',
  $13: '∞',
  $14: '⩲',
  $15: '⩱',
  $16: '±',
  $17: '∓',
  $18: '+-',
  $19: '-+',
  $22: '⨀',
  $23: '⨀',
  $32: '⟳',
  $33: '⟳',
  $36: '→',
  $37: '→',
  $40: '↑',
  $41: '↑',
  $44: '∞/=',
  $45: '=/∞',
  $132: '⇆',
  $133: '⇆',
  $138: '⊕',
  $146: 'N',
  $222: '⇈',
  $256: '=/∞',
};

/**
 * Object of NAG Regexp
 *
 * @type {object}
 */
export const NAGRegexp = {
  '??': '?\\?',
  '!?': '!\\?',
  '-+': '-\\+',
  '∞/=': '∞\\/\\=',
  '=/∞': '=\\/\\∞',
  '=': 's=', // eslint-disable-line no-useless-escape
  N: 'N\\b',
};

/**
 * NAG without space before move
 *
 * @type {array}
 */
export const NAGWithoutSpace = ['!', '?', '!!', '??', '!?', '?!', '='];

/**
 * Return an inverted nagMap
 */
export const invertedNagMap = <{ [key: string]: Annotations.Nags }>invertObject(nagMap);

/**
 * Annotations map for context menu
 *
 * notation codes are in Numeric Annotation Glyph (NAG) format:
 * https://en.wikipedia.org/wiki/Numeric_Annotation_Glyphs
 */
export const annotations = [
  {
    action: HMLContextMenu.AnnotationActions.AddAnnotation,
    code: Annotations.Nags.$1,
    display: nagMap.$1,
    meaning: MoveList.Translation.Annotations.GoodMove,
  },
  {
    action: HMLContextMenu.AnnotationActions.AddAnnotation,
    code: Annotations.Nags.$2,
    display: nagMap.$2,
    meaning: MoveList.Translation.Annotations.BadMove,
  },
  {
    action: HMLContextMenu.AnnotationActions.AddAnnotation,
    code: Annotations.Nags.$3,
    display: nagMap.$3,
    meaning: MoveList.Translation.Annotations.ExcellentMove,
  },
  {
    action: HMLContextMenu.AnnotationActions.AddAnnotation,
    code: Annotations.Nags.$4,
    display: nagMap.$4,
    meaning: MoveList.Translation.Annotations.Blunder,
  },
  {
    action: HMLContextMenu.AnnotationActions.AddAnnotation,
    code: Annotations.Nags.$5,
    display: nagMap.$5,
    meaning: MoveList.Translation.Annotations.SpeculativeMove,
  },
  {
    action: HMLContextMenu.AnnotationActions.AddAnnotation,
    code: Annotations.Nags.$6,
    display: nagMap.$6,
    meaning: MoveList.Translation.Annotations.DubiousMove,
  },
  {
    action: HMLContextMenu.AnnotationActions.AddAdditionalAnnotation,
    code: Annotations.Nags.$9,
    display: nagMap.$9,
    meaning: MoveList.Translation.Annotations.Miss,
  },
  {
    action: HMLContextMenu.AnnotationActions.AddAdditionalAnnotation,
    code: Annotations.Nags.$10,
    display: nagMap.$10,
    meaning: MoveList.Translation.Annotations.Drawish,
  },
  {
    action: HMLContextMenu.AnnotationActions.AddAdditionalAnnotation,
    code: Annotations.Nags.$13,
    display: nagMap.$13,
    meaning: MoveList.Translation.Annotations.Unclear,
  },
  {
    action: HMLContextMenu.AnnotationActions.AddAdditionalAnnotation,
    code: Annotations.Nags.$14 /* += */,
    display: nagMap.$14,
    meaning: MoveList.Translation.Annotations.WhiteHasSlightAdvantage,
  },
  {
    action: HMLContextMenu.AnnotationActions.AddAdditionalAnnotation,
    code: Annotations.Nags.$15 /* =+ */,
    display: nagMap.$15,
    meaning: MoveList.Translation.Annotations.BlackHasSlightAdvantage,
  },
  {
    action: HMLContextMenu.AnnotationActions.AddAdditionalAnnotation,
    code: Annotations.Nags.$16 /* moderate adv w */,
    display: nagMap.$16,
    meaning: MoveList.Translation.Annotations.WhiteHasModerateAdvantage,
  },
  {
    action: HMLContextMenu.AnnotationActions.AddAdditionalAnnotation,
    code: Annotations.Nags.$17 /* moderate adv b */,
    display: nagMap.$17,
    meaning: MoveList.Translation.Annotations.BlackHasModerateAdvantage,
  },
  {
    action: HMLContextMenu.AnnotationActions.AddAdditionalAnnotation,
    code: Annotations.Nags.$18,
    display: nagMap.$18,
    meaning: MoveList.Translation.Annotations.WhiteHasDecisiveAdvantage,
  },
  {
    action: HMLContextMenu.AnnotationActions.AddAdditionalAnnotation,
    code: Annotations.Nags.$19,
    display: nagMap.$19,
    meaning: MoveList.Translation.Annotations.BlackHasDecisiveAdvantage,
  },
  {
    action: HMLContextMenu.AnnotationActions.AddAdditionalAnnotation,
    code: Annotations.Nags.$22,
    display: nagMap.$22,
    meaning: MoveList.Translation.Annotations.PositionInZuqZwang,
  },
  {
    action: HMLContextMenu.AnnotationActions.AddAdditionalAnnotation,
    code: Annotations.Nags.$36,
    display: nagMap.$36,
    meaning: MoveList.Translation.Annotations.WithInitiative,
  },
  {
    action: HMLContextMenu.AnnotationActions.AddAdditionalAnnotation,
    code: Annotations.Nags.$40,
    display: nagMap.$40,
    meaning: MoveList.Translation.Annotations.WithCompensation,
  },
  {
    action: HMLContextMenu.AnnotationActions.AddAdditionalAnnotation,
    code: Annotations.Nags.$146,
    display: nagMap.$146,
    meaning: MoveList.Translation.Annotations.Novelty,
  },
  {
    action: HMLContextMenu.AnnotationActions.AddAdditionalAnnotation,
    code: Annotations.Nags.$256,
    display: nagMap.$256,
    meaning: MoveList.Translation.Annotations.WithAttack,
  },
];

/**
 * Annotations map for context menu
 *
 * notation codes are in Numeric Annotation Glyph (NAG) format:
 * https://en.wikipedia.org/wiki/Numeric_Annotation_Glyphs
 */
export const contextMenuAnnotations = [
  {
    action: CONTEXT_MENU_ACTION_NAMES.AddAnnotation,
    code: Annotations.Nags.$3,
    display: nagMap.$3,
    meaning: MoveList.Translation.Annotations.ExcellentMove,
  },
  {
    action: CONTEXT_MENU_ACTION_NAMES.AddAnnotation,
    code: Annotations.Nags.$1,
    display: nagMap.$1,
    meaning: MoveList.Translation.Annotations.GoodMove,
  },
  {
    action: CONTEXT_MENU_ACTION_NAMES.AddAnnotation,
    code: Annotations.Nags.$5,
    display: nagMap.$5,
    meaning: MoveList.Translation.Annotations.SpeculativeMove,
  },
  {
    action: CONTEXT_MENU_ACTION_NAMES.AddAnnotation,
    code: Annotations.Nags.$6,
    display: nagMap.$6,
    meaning: MoveList.Translation.Annotations.DubiousMove,
  },
  {
    action: CONTEXT_MENU_ACTION_NAMES.AddAnnotation,
    code: Annotations.Nags.$2,
    display: nagMap.$2,
    meaning: MoveList.Translation.Annotations.BadMove,
  },
  {
    action: CONTEXT_MENU_ACTION_NAMES.AddAnnotation,
    code: Annotations.Nags.$4,
    display: nagMap.$4,
    meaning: MoveList.Translation.Annotations.Blunder,
  },
  {
    action: CONTEXT_MENU_ACTION_NAMES.AddAdditionalAnnotation,
    code: Annotations.Nags.$9,
    display: nagMap.$9,
    meaning: MoveList.Translation.Annotations.Miss,
  },
  {
    action: CONTEXT_MENU_ACTION_NAMES.AddAdditionalAnnotation,
    code: Annotations.Nags.$18,
    display: nagMap.$18,
    meaning: MoveList.Translation.Annotations.WhiteHasDecisiveAdvantage,
  },
  {
    action: CONTEXT_MENU_ACTION_NAMES.AddAdditionalAnnotation,
    code: Annotations.Nags.$16 /* moderate adv w */,
    display: nagMap.$16,
    meaning: MoveList.Translation.Annotations.WhiteHasModerateAdvantage,
  },
  {
    action: CONTEXT_MENU_ACTION_NAMES.AddAdditionalAnnotation,
    code: Annotations.Nags.$14 /* += */,
    display: nagMap.$14,
    meaning: MoveList.Translation.Annotations.WhiteHasSlightAdvantage,
  },
  {
    action: CONTEXT_MENU_ACTION_NAMES.AddAdditionalAnnotation,
    code: Annotations.Nags.$10,
    display: nagMap.$10,
    meaning: MoveList.Translation.Annotations.Drawish,
  },
  {
    action: CONTEXT_MENU_ACTION_NAMES.AddAdditionalAnnotation,
    code: Annotations.Nags.$15 /* =+ */,
    display: nagMap.$15,
    meaning: MoveList.Translation.Annotations.BlackHasSlightAdvantage,
  },
  {
    action: CONTEXT_MENU_ACTION_NAMES.AddAdditionalAnnotation,
    code: Annotations.Nags.$17 /* moderate adv b */,
    display: nagMap.$17,
    meaning: MoveList.Translation.Annotations.BlackHasModerateAdvantage,
  },
  {
    action: CONTEXT_MENU_ACTION_NAMES.AddAdditionalAnnotation,
    code: Annotations.Nags.$19,
    display: nagMap.$19,
    meaning: MoveList.Translation.Annotations.BlackHasDecisiveAdvantage,
  },
  {
    action: CONTEXT_MENU_ACTION_NAMES.AddAdditionalAnnotation,
    code: Annotations.Nags.$13,
    display: nagMap.$13,
    meaning: MoveList.Translation.Annotations.Unclear,
  },
  {
    action: CONTEXT_MENU_ACTION_NAMES.AddAdditionalAnnotation,
    code: Annotations.Nags.$45,
    display: nagMap.$45,
    meaning: MoveList.Translation.Annotations.WithCompensation,
  },
  {
    action: CONTEXT_MENU_ACTION_NAMES.AddAdditionalAnnotation,
    code: Annotations.Nags.$146,
    display: nagMap.$146,
    meaning: MoveList.Translation.Annotations.Novelty,
  },
  {
    action: CONTEXT_MENU_ACTION_NAMES.AddAdditionalAnnotation,
    code: Annotations.Nags.$22,
    display: nagMap.$22,
    meaning: MoveList.Translation.Annotations.PositionInZuqZwang,
  },
  {
    action: CONTEXT_MENU_ACTION_NAMES.AddAdditionalAnnotation,
    code: Annotations.Nags.$138,
    display: nagMap.$138,
    meaning: MoveList.Translation.Annotations.TimeTrouble,
  },
  {
    display: '', // used for spacing in the context menu
  },
  {
    action: CONTEXT_MENU_ACTION_NAMES.AddAdditionalAnnotation,
    code: Annotations.Nags.$222,
    display: nagMap.$222,
    meaning: MoveList.Translation.Annotations.DevelopmentLead,
  },
  {
    action: CONTEXT_MENU_ACTION_NAMES.AddAdditionalAnnotation,
    code: Annotations.Nags.$40,
    display: nagMap.$40,
    meaning: MoveList.Translation.Annotations.WithInitiative,
  },
  {
    action: CONTEXT_MENU_ACTION_NAMES.AddAdditionalAnnotation,
    code: Annotations.Nags.$36,
    display: nagMap.$36,
    meaning: MoveList.Translation.Annotations.WithAttack,
  },
  {
    action: CONTEXT_MENU_ACTION_NAMES.AddAdditionalAnnotation,
    code: Annotations.Nags.$132,
    display: nagMap.$132,
    meaning: MoveList.Translation.Annotations.Counterplay,
  },
];
